import React, { useEffect, useMemo, useState } from "react";
import LibraryPeriodSelect from "./LibraryPeriodSelect";
import RfpJobRow from "./RfpJobRow";
import {
  filterLibraryJobs,
  LIBRARY_ROW_GRID,
  sortLibraryJobsByRecency,
  uniqueCountries,
  uniqueOwnerSes,
} from "./rfpUtils";

export default function RfpLibraryModal({
  open,
  onClose,
  jobs,
  activeJobId,
  onOpen,
  libraryPeriod = "12m",
  availableYears = [],
  onLibraryPeriodChange,
}) {
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const ownerOptions = useMemo(() => uniqueOwnerSes(jobs), [jobs]);
  const countryOptions = useMemo(() => uniqueCountries(jobs), [jobs]);
  const filtered = useMemo(
    () => filterLibraryJobs(jobs, { search, ownerSe: ownerFilter, country: countryFilter }),
    [jobs, search, ownerFilter, countryFilter]
  );
  const sorted = useMemo(() => sortLibraryJobsByRecency(filtered), [filtered]);

  if (!open) return null;

  const fieldCls =
    "rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-950";

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 p-3 sm:items-center" role="dialog" aria-modal="true">
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div>
            <h3 className="text-base font-semibold">All RFPs</h3>
            <p className="text-xs text-slate-500">{sorted.length} of {jobs.length} shown</p>
          </div>
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm dark:border-slate-600"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <LibraryPeriodSelect
            period={libraryPeriod}
            years={availableYears}
            onChange={onLibraryPeriodChange}
            className={fieldCls}
          />
          <input
            type="search"
            placeholder="Search customer…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={`${fieldCls} min-w-[12rem] flex-1`}
          />
          <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} className={fieldCls}>
            <option value="">All Owner SE</option>
            {ownerOptions.map((se) => (
              <option key={se} value={se}>{se}</option>
            ))}
          </select>
          <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} className={fieldCls}>
            <option value="">All countries</option>
            {countryOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          <div className={`mb-1 hidden gap-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400 sm:grid ${LIBRARY_ROW_GRID}`}>
            <span>Customer</span>
            <span>Country</span>
            <span>Owner SE</span>
            <span>Updated</span>
            <span className="justify-self-end">Status</span>
            <span className="justify-self-end">Downloads</span>
          </div>
          {sorted.length === 0 ? (
            <p className="px-4 py-10 text-center text-sm text-slate-500">No jobs match your search.</p>
          ) : (
            sorted.map((job) => (
              <RfpJobRow
                key={job.job_id}
                job={job}
                active={job.job_id === activeJobId}
                dense
                onOpen={(id) => {
                  onOpen(id);
                  onClose();
                }}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
